import {
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { type FC, type MutableRefObject, type ReactNode, useRef } from 'react';

import { Button } from '../Button/Button';
import { Modal, ModalFooter } from '../Modal/Modal';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info' | 'question';

export interface ConfirmDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when the dialog should close
   */
  onClose: () => void;

  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void;

  /**
   * Dialog title
   */
  title: string;

  /**
   * Dialog message/description
   */
  message: ReactNode;

  /**
   * Confirm button text
   * @default 'Confirm'
   */
  confirmText?: string;

  /**
   * Cancel button text
   * @default 'Cancel'
   */
  cancelText?: string;

  /**
   * Dialog variant that affects icon and button styling
   * @default 'question'
   */
  variant?: ConfirmDialogVariant;

  /**
   * Whether the confirm action is in progress
   * @default false
   */
  isLoading?: boolean;
}

const variantConfig = {
  danger: {
    icon: TrashIcon,
    iconBgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    buttonVariant: 'danger' as const,
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconBgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    buttonVariant: 'primary' as const,
  },
  info: {
    icon: InformationCircleIcon,
    iconBgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    buttonVariant: 'primary' as const,
  },
  question: {
    icon: QuestionMarkCircleIcon,
    iconBgColor: 'bg-gray-100 dark:bg-gray-700',
    iconColor: 'text-gray-600 dark:text-gray-400',
    buttonVariant: 'primary' as const,
  },
};

/**
 * ConfirmDialog component
 *
 * A modal dialog for confirming user actions. Replaces native window.confirm().
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ConfirmDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={() => {
 *     handleDelete();
 *     setIsOpen(false);
 *   }}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item? This action cannot be undone."
 *   variant="danger"
 *   confirmText="Delete"
 * />
 * ```
 */
export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'question',
  isLoading = false,
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      initialFocus={cancelButtonRef as MutableRefObject<HTMLElement | null>}
    >
      <div className="flex gap-4">
        <div
          className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-full ${config.iconBgColor}`}
        >
          <Icon className={`w-6 h-6 ${config.iconColor}`} aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">{message}</div>
        </div>
      </div>

      <ModalFooter>
        <Button
          ref={cancelButtonRef}
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          type="button"
          variant={config.buttonVariant}
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? 'Please wait...' : confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConfirmDialog.displayName = 'ConfirmDialog';
